/*
Muestra la barra de filtros de búsqueda
Autor: Benjamín León
Fecha: 2025-01-20
*/
import React, { useState } from 'react'

import { FormatProps, handleInputChangeFormat } from '../utils/handleInputChangeFormat'
import { useAuth } from '../hooks/useAuth'
import fuelTranslate from '../utils/fuelTranslate'
import gearboxTranslate from '../utils/gearboxTranslate'
import { getAllBodyTypes } from '../utils/vehicleAttributeTranslate'
import { VehicleFilterForm } from '../interfaces/CardOne'

import { SelectField } from './SelectField'
import SelectBranchField from './SelectBranchField'
import { FormFields, InputFieldProps } from './FormFields'

const formatProps: FormatProps[] = [
  { name: 'yearFrom', name_format: 'yearFrom_format', isInteger: true },
  { name: 'yearTo', name_format: 'yearTo_format', isInteger: true },
  { name: 'priceFrom', name_format: 'priceFrom_format' },
  { name: 'priceTo', name_format: 'priceTo_format' },
  { name: 'kmFrom', name_format: 'kmFrom_format' },
  { name: 'kmTo', name_format: 'kmTo_format' },
]

const VehicleFilter: React.FC<{
  formData: VehicleFilterForm
  setFormData: React.Dispatch<React.SetStateAction<VehicleFilterForm | null>>
}> = ({ formData, setFormData }) => {
  const { userData } = useAuth()
  const [showFilters, setShowFilters] = useState<boolean>(false)
  const fields: Array<InputFieldProps> = [
    { label: 'Año desde', name: 'yearFrom_format', value: formData?.yearFrom_format ?? '', type: 'string' },
    { label: 'Año hasta', name: 'yearTo_format', value: formData?.yearTo_format ?? '', type: 'string' },
    { label: 'Precio desde', name: 'priceFrom_format', value: formData?.priceFrom_format ?? '', type: 'string' },
    { label: 'Precio hasta', name: 'priceTo_format', value: formData?.priceTo_format ?? '', type: 'string' },
    { label: 'Km. desde', name: 'kmFrom_format', value: formData?.kmFrom_format ?? '', type: 'string' },
    { label: 'Km. hasta', name: 'kmTo_format', value: formData?.kmTo_format ?? '', type: 'string' },
  ]
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    handleInputChangeFormat<VehicleFilterForm>(e, formData, setFormData, formatProps)
  }
  const tractionTranslate: Record<string, string> = {
    fwd: '4x2',
    awd: '4x4',
  }

  // Create comprehensive body type options using all available body types
  const bodyTypeOptions = React.useMemo(() => {
    const allBodyTypes = getAllBodyTypes()
    const options: Record<string, string> = {}

    // Convert array to options object for SelectField component
    allBodyTypes.forEach((bodyType) => {
      options[bodyType] = bodyType
    })

    return options
  }, [])

  const label = showFilters ? 'Esconder filtros de búsqueda' : 'Ver filtros de búsqueda'

  return (
    <>
      <div className="flex justify-center mb-2">
        <button className="text-blue-500 font-bold" onClick={() => setShowFilters(!showFilters)} aria-label="Ver">
          {label}
        </button>
      </div>

      {showFilters && (
        <div className="grid grid-cols-1 sm:grid-cols-6 lg:grid-cols-12 gap-x-2">
          <FormFields fields={fields} handleInputChange={handleInputChange} />
          <div className="col-span-1 sm:col-span-2 lg:col-span-2">
            <SelectBranchField
              label="Sucursal"
              name="branch"
              value={formData?.branch}
              options={userData?.branches}
              handleInputChange={handleInputChange}
              firstOptionText="-- Todas --"
            />
          </div>
          <SelectField
            label="Carrocería"
            name="body"
            value={formData?.body}
            options={bodyTypeOptions}
            handleInputChange={handleInputChange}
            firstOptionText="-- Todas --"
          />
          <SelectField
            label="Combustible"
            name="fuel"
            value={formData?.fuel}
            options={fuelTranslate}
            handleInputChange={handleInputChange}
            firstOptionText="-- Todos --"
          />
          <SelectField
            label="Transmisión"
            name="gearbox"
            value={formData?.gearbox}
            options={gearboxTranslate}
            handleInputChange={handleInputChange}
            firstOptionText="-- Todas --"
          />
          <SelectField
            label="Tracción"
            name="traction"
            value={formData?.traction}
            options={tractionTranslate}
            handleInputChange={handleInputChange}
            firstOptionText="-- Todos --"
          />
        </div>
      )}
    </>
  )
}

export default VehicleFilter
